// TF-2 Phase 4b — Resumable chunked-upload helper (frontend).
//
// Public surface:
//   const { uploadId, evidence } = await tfChunkedUpload({ caseId, file, manifest, onProgress, signal });
//
// `manifest` is the OpenUploadBody payload minus declared_size_bytes/declared_filename/declared_sha256
//   (these are derived from `file`).  Accepted manifest keys:
//     kind, title, confidentiality_treatment, kind_description, tags, declared_mime,
//     parent_evidence_id, change_note
//
// `onProgress({ receivedBytes, totalBytes, percent, phase })` is called between PATCHes.
// `signal` is an AbortSignal — if it fires we DELETE the upload manifest and reject.
//
// Behaviour:
//   - 3-way concurrent PATCH (or `navigator.hardwareConcurrency` if smaller).
//   - Up to 5 retries per chunk with exponential backoff (250 ms × 2^attempt).
//   - Full-file SHA-256 computed in the browser (uses SubtleCrypto if available).
//   - On any unrecoverable error the upload manifest is abandoned (best-effort DELETE).

import { api } from './api';

const DEFAULT_CONCURRENCY = 3;
const MAX_RETRIES_PER_CHUNK = 5;
const RETRY_BASE_MS = 250;

async function sha256Hex(blob) {
  if (!window.crypto || !window.crypto.subtle) return null;
  try {
    const buf = await blob.arrayBuffer();
    const hash = await window.crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(hash))
      .map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return null;
  }
}

function sleep(ms) { return new Promise((r) => setTimeout(r, ms)); }

async function patchChunk({ baseURL, uploadId, chunk, start, end, total, signal, authHeader }) {
  // We bypass axios here so we can stream raw bytes without JSON coercion.
  const url = `${baseURL.replace(/\/$/, '')}/admin/talentfix/uploads/${uploadId}`;
  const headers = {
    'Content-Type': 'application/octet-stream',
    'Content-Range': `bytes ${start}-${end}/${total}`,
  };
  if (authHeader) headers['Authorization'] = authHeader;
  const r = await fetch(url, {
    method: 'PATCH',
    body: chunk,
    headers,
    credentials: 'include',
    signal,
  });
  if (!r.ok) {
    const text = await r.text().catch(() => '');
    const err = new Error(`patch_${r.status}: ${text.slice(0, 200)}`);
    err.status = r.status;
    throw err;
  }
  return r.json();
}

export async function tfChunkedUpload({ caseId, file, manifest, onProgress, signal }) {
  if (!file || !(file instanceof Blob)) throw new Error('file_required');
  if (!caseId) throw new Error('caseId_required');
  if (!manifest?.kind || !manifest?.title) throw new Error('manifest_kind_and_title_required');

  // 1. Compute SHA-256 (best effort)
  onProgress?.({ phase: 'hashing', receivedBytes: 0, totalBytes: file.size, percent: 0 });
  const declared_sha256 = await sha256Hex(file);

  // 2. Open manifest
  const openBody = {
    ...manifest,
    declared_filename: file.name || 'file',
    declared_size_bytes: file.size,
    declared_mime: manifest.declared_mime || file.type || null,
    declared_sha256: declared_sha256 || undefined,
  };
  const openResp = await api.post(
    `/admin/talentfix/cases/${caseId}/uploads`, openBody,
  );
  const { upload_id: uploadId, chunk_size_bytes: chunkSize } = openResp.data;
  if (!uploadId || !chunkSize) throw new Error('open_upload_invalid_response');

  const baseURL = api.defaults.baseURL;
  // axios stores Authorization on default headers if cookie auth isn't in use — both supported.
  const authHeader = api.defaults.headers?.common?.Authorization || null;

  const total = file.size;
  const totalChunks = Math.ceil(total / chunkSize);
  const queue = [];
  for (let i = 0; i < totalChunks; i += 1) {
    const start = i * chunkSize;
    const end = Math.min(start + chunkSize, total) - 1;
    queue.push({ index: i, start, end });
  }

  let received = 0;
  const concurrency = Math.max(1, Math.min(
    DEFAULT_CONCURRENCY,
    (typeof navigator !== 'undefined' && navigator.hardwareConcurrency) || DEFAULT_CONCURRENCY,
  ));

  // Cleanup on abort
  const onAbort = async () => {
    try { await api.delete(`/admin/talentfix/uploads/${uploadId}`); } catch { /* best effort */ }
  };
  if (signal) {
    signal.addEventListener('abort', onAbort, { once: true });
  }

  // 3. Run worker pool
  const sendOne = async (job) => {
    const chunkBlob = file.slice(job.start, job.end + 1);
    let attempt = 0;
    while (true) {
      try {
        await patchChunk({
          baseURL, uploadId, chunk: chunkBlob,
          start: job.start, end: job.end, total, signal, authHeader,
        });
        received += (job.end - job.start + 1);
        onProgress?.({
          phase: 'uploading',
          receivedBytes: received,
          totalBytes: total,
          percent: Math.min(99, Math.floor((received / total) * 100)),
        });
        return;
      } catch (err) {
        if (signal?.aborted) throw err;
        // 4xx (client-side mistake) → don't retry except 408/425/429/503
        const transient = !err.status
          || [408, 425, 429, 500, 502, 503, 504].includes(err.status);
        if (!transient || attempt >= MAX_RETRIES_PER_CHUNK) throw err;
        attempt += 1;
        await sleep(RETRY_BASE_MS * (2 ** attempt));
      }
    }
  };

  // Run `concurrency` workers in parallel, each popping from the queue
  const workers = Array.from({ length: concurrency }).map(async () => {
    while (queue.length > 0) {
      const job = queue.shift();
      if (!job) break;
      await sendOne(job);
    }
  });
  try {
    await Promise.all(workers);
  } finally {
    if (signal) signal.removeEventListener('abort', onAbort);
  }

  // 4. Finalise
  onProgress?.({ phase: 'finalising', receivedBytes: total, totalBytes: total, percent: 99 });
  const completeResp = await api.post(
    `/admin/talentfix/uploads/${uploadId}/complete`,
  );
  onProgress?.({ phase: 'done', receivedBytes: total, totalBytes: total, percent: 100 });
  return { uploadId, evidence: completeResp.data };
}

export async function tfAbandonUpload(uploadId) {
  try { await api.delete(`/admin/talentfix/uploads/${uploadId}`); }
  catch { /* swallow */ }
}

export async function tfGetUploadStatus(uploadId) {
  return api.get(`/admin/talentfix/uploads/${uploadId}`).then((r) => r.data);
}
